import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Box, FormControl, InputLabel, Select, MenuItem, Button, DialogActions, DialogContent } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VideocamIcon from '@mui/icons-material/Videocam';
import { initialiseDeviceSettings } from '../../store/settings/settingsSlice';
import { setSelectedMic, setSelectedSpeaker, setSelectedCamera, setSelectedEffect, setSelectedBackground } from "../../../MediaControls/store/mediaControlSlice";
import { setSettingsMic, setSettingsSpeaker, setSettingsCamera, setSettingsEffect, setSettingsBackground, setActiveTab } from "../../store/settings/settingsSlice";
import { useCameraTrack } from '../../../../hooks/useCameraTrack';
import { useMicrophoneTrack } from '../../../../hooks/useMicrophoneTrack';
import { useCameraEffect } from '../../../../hooks/useCameraEffect';
import { useTranslation } from 'react-i18next';
import { getBackgrounds, getDefaultBackgrounds, uploadBackground, deleteBackground, uploadDefaultBackground, deleteDefaultBackground } from '../../store/settings/settingsThunks';
import ActionConfirmation from '../../../../components/ActionConfirmation';
import { CircularProgress } from '@mui/material';
import './Settings.css'
import { tenantHasFeature } from '../../../Auth/utils/AuthUtils';
import { addNotification } from '../../../Notifications/store/notificationsSlice';
import { ReactComponent as DeleteIcon } from "../../../../assets/delete_icon.svg";
import { setTestModal } from '../../store/controlPanelSlice';
import NotificationBuilder from '../../../Notifications/utils/NotificationBuilder';
export default function Settings({ onClose, isCameraEffectEnabled = true }) {
  const [open, setOpen] = React.useState(true);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { localAvatar } = useSelector((state) => state.auth);
  const { selectedMic, selectedSpeaker, selectedCamera, selectedEffect, selectedBackground, mics, speakers, cameras } = useSelector((state) => state.mediaControls);
  const { activeTab, selectedSettingsMic, selectedSettingsSpeaker, selectedSettingsCamera, selectedSettingsEffect, selectedSettingsBackground, defaultBackgrounds, customBackgrounds, publicDefaultBackgrounds } = useSelector((state) => state.settingsModal);
  const initRef = useRef(false);

  const fileInputRef = useRef(null);
  const fileDefaultInputRef = useRef(null);
  // --- Refs for the video elements ---
  const cameraRef = useRef(null); // This remains the ref for the VISIBLE video element.
  const hiddenVideoRef = useRef(null); // NEW: Ref for the hidden video element used for processing.
  const hiddenCanvasRef = useRef(null); // NEW: Ref for the hidden canvas element.
  const backgroundImageRef = useRef(null); // NEW: Ref for the background image element.
  const [isImageReady, setIsImageReady] = useState(false);
  const [backgroundToDelete, setBackgroundToDelete] = useState(null); // State for background to delete
  const [defaulBtackgroundToDelete, setDefaultbackgroundToDelete] = useState(null); // State for background to delete

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false); // State for delete confirmation modal

  // --- Hook Chain & State ---
  // 1. Get the raw track from the camera. This is unchanged.
  const { track: rawCameraTrack, loading: cameraLoading, error: cameraError } = useCameraTrack(selectedSettingsCamera);

  // 2. State to hold the output track from the effects hook.
  const [cameraTrack, setCameraTrack] = useState(null);

  // 3. Callback for the effects hook to deliver the processed stream.
  const handleStreamReady = useCallback((stream) => {
    // We only care about the video track from the stream.
    const newTrack = stream ? stream.getVideoTracks()[0] : null;
    setCameraTrack(newTrack);
  }, [activeTab]);

  // 4. Call the effects hook. This runs in the background.
  useCameraEffect({
    track: rawCameraTrack,
    effect: selectedSettingsEffect,
    videoRef: hiddenVideoRef,
    canvasRef: hiddenCanvasRef,
    onStreamReady: handleStreamReady,
    frameSkip: 3,
    backgroundImageRef,
    isImageReady
  });


  // 6. This final useEffect is identical to your original one.
  // It attaches the final, correct `cameraTrack` to the visible video element.
  useEffect(() => {
    if (cameraRef.current && cameraTrack) {
      const currentStream = cameraRef.current.srcObject;
      // Only update srcObject if the track has actually changed.
      if (!currentStream || currentStream.getVideoTracks()[0] !== cameraTrack) {
        cameraRef.current.srcObject = new MediaStream([cameraTrack]);
        cameraRef.current.play().catch(e => console.error("Error playing final stream:", e));
      }
    } else if (cameraRef.current) {
      // Clear the video element if there's no track
      cameraRef.current.srcObject = null;
    }
  }, [cameraTrack, activeTab]);

  const { track: audioTrack, loading: micLoading, error: micError } = useMicrophoneTrack(selectedSettingsMic);



  // Volume monitoring state
  const inputVolumeBarRef = useRef(0);
  const [outputVolume, setOutputVolume] = useState(0);
  const audioContextRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Monitor microphone input volume
  useEffect(() => {
    // --- Step 1: Define local variables for the audio graph ---
    let audioContext;
    let analyser;
    let source;

    // The animation loop function remains the same
    const monitorAudio = () => {
      // Check that the analyser and the DOM element are ready
      if (analyser && inputVolumeBarRef.current) {
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);

        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;
        const volume = Math.min(100, (average / 128) * 100);

        // Update style directly on the DOM element
        inputVolumeBarRef.current.style.width = volume + '%';
        inputVolumeBarRef.current.style.backgroundSize = volume > 1 ? `${10000 / volume}% 100%` : '0% 100%';
      }

      // Continue the loop
      animationFrameRef.current = requestAnimationFrame(monitorAudio);
    };

    // --- Step 2: Set up the audio graph if we have a track ---
    if (audioTrack) {
      try {
        audioContext = new (window.AudioContext || window?.webkitAudioContext)();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;

        // Create the source from the track
        const stream = new MediaStream([audioTrack]);
        source = audioContext.createMediaStreamSource(stream);

        // Connect the source to the analyser
        source.connect(analyser);

        // Start the animation loop
        monitorAudio();

      } catch (error) {
        console.error('Error setting up audio analysis:', error);
      }
    }

    // --- Step 3: The crucial cleanup function ---
    // This runs when the component unmounts OR before the effect re-runs (i.e., when audioTrack changes)
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (source) {
        source.disconnect();
      }
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close();
      }
      // Also reset the bar visually upon cleanup
      if (inputVolumeBarRef.current) {
        inputVolumeBarRef.current.style.width = '0%';
      }
    };
  }, [audioTrack]);



  // Cleanup audio context on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true;
      dispatch(getBackgrounds({ params: [{ id: localAvatar.id }] }));
      dispatch(getDefaultBackgrounds());
      dispatch(initialiseDeviceSettings({
        selectedEffect,
        selectedBackground,
      }));
    }
  }, []);

  const handleSave = () => {
    //If there is a change in each of the settings, update the media control slice case by case
    if (selectedSettingsMic?.deviceId !== selectedMic?.deviceId) {
      dispatch(setSelectedMic(selectedSettingsMic));
    }
    if (selectedSettingsSpeaker?.deviceId !== selectedSpeaker?.deviceId) {
      dispatch(setSelectedSpeaker(selectedSettingsSpeaker));
    }
    if (selectedSettingsCamera?.deviceId !== selectedCamera?.deviceId) {
      dispatch(setSelectedCamera(selectedSettingsCamera));
    }
    if (selectedSettingsEffect !== selectedEffect) {
      dispatch(setSelectedEffect(selectedSettingsEffect));
    }
    if (selectedSettingsBackground !== selectedBackground) {
      dispatch(setSelectedBackground(selectedSettingsBackground));
    }

    onClose();
  }

  const handleFileChange = async (event) => {
    const MAX_WIDTH = 2000;
    const MAX_SIZE = 5 * 1024 * 1024;
    const MIN_QUALITY = 0.3;

    try {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      let imageBitmap = await createImageBitmap(file);
      let width = imageBitmap.width;
      let height = imageBitmap.height;


      let canvas = document.createElement("canvas");
      let ctx = canvas.getContext("2d");

      if (width > MAX_WIDTH) {
        const ratio = MAX_WIDTH / width;
        width = MAX_WIDTH;
        height = Math.round(height * ratio);

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(imageBitmap, 0, 0, width, height);

      } else {
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(imageBitmap, 0, 0);
      }

      let quality = 0.90;
      let blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", quality)
      );
      if (!blob) {
        console.error("❌ toBlob returned null");
        return;
      }

      while (blob.size > MAX_SIZE && quality > MIN_QUALITY) {
        quality -= 0.05;

        blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", quality)
        );
        if (!blob) break;
      }
      if (blob.size > MAX_SIZE || !blob) {
        const notification = NotificationBuilder.error()
          .withHeader('TAILLE_MAXIMALE_FICHIER', true)
          .build();
        dispatch(addNotification(notification));

        return;
      }

      const formData = new FormData();
      formData.append('file', blob, file.name);
      formData.append('id', localAvatar.id);

      dispatch(uploadBackground({
        body: formData,
      }))

      // Reset input field after successful upload
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error("Upload failed:", error);
      const notification = NotificationBuilder.error()
        .withHeader('upload_error', true)
        .withLucideIcon('cloud-off')
        .build();
      dispatch(addNotification(notification));
    }
  };
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };
  const handleDefaultFileChange = async (event) => {
    const MAX_WIDTH = 2000;
    const MAX_SIZE = 5 * 1024 * 1024;
    const MIN_QUALITY = 0.3;

    try {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      let imageBitmap = await createImageBitmap(file);
      let width = imageBitmap.width;
      let height = imageBitmap.height;


      let canvas = document.createElement("canvas");
      let ctx = canvas.getContext("2d");

      if (width > MAX_WIDTH) {
        const ratio = MAX_WIDTH / width;
        width = MAX_WIDTH;
        height = Math.round(height * ratio);

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(imageBitmap, 0, 0, width, height);

      } else {
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(imageBitmap, 0, 0);
      }

      let quality = 0.90;
      let blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", quality)
      );
      if (!blob) {
        console.error("❌ toBlob returned null");
        return;
      }

      while (blob.size > MAX_SIZE && quality > MIN_QUALITY) {
        quality -= 0.05;

        blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", quality)
        );
        if (!blob) break;
      }
      if (blob.size > MAX_SIZE || !blob) {
        const notification = NotificationBuilder.error()
          .withHeader('TAILLE_MAXIMALE_FICHIER', true)
          .build();
        dispatch(addNotification(notification));

        return;
      }

      const formData = new FormData();
      formData.append('file', blob, file.name);
      dispatch(uploadDefaultBackground({
        body: formData,
      }))
    } catch (error) {
      console.error("Upload failed:", error);

    }
  }

  const handleTestOutputAudio = useCallback(async () => {
    try {
      // Create a test audio context
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Create a simple test tone (440Hz - A note)
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const analyser = audioContext.createAnalyser();

      // Configure the oscillator
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(440, audioContext.currentTime);

      // Configure analyser for output monitoring
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;

      // Configure volume (start at 0, ramp up, then down)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 1);

      // Connect the nodes
      oscillator.connect(gainNode);
      gainNode.connect(analyser);

      // Create a MediaStreamDestination to capture the audio
      const destination = audioContext.createMediaStreamDestination();
      gainNode.connect(destination);

      // Create an audio element to play the stream
      const testAudio = new Audio();
      testAudio.srcObject = destination.stream;
      testAudio.volume = 0.3;

      // Set output device if available
      if (selectedSettingsSpeaker?.deviceId && testAudio.setSinkId) {
        await testAudio.setSinkId(selectedSettingsSpeaker.deviceId);
      }

      // Monitor output volume during test
      const monitorOutput = () => {
        if (analyser && audioContext.state !== 'closed') {
          const bufferLength = analyser.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          analyser.getByteFrequencyData(dataArray);

          // Calculate average volume
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            sum += dataArray[i];
          }
          const average = sum / bufferLength;
          const volume = Math.min(100, (average / 128) * 100);

          setOutputVolume(volume);

          if (audioContext.currentTime < audioContext.currentTime + 1.2) {
            requestAnimationFrame(monitorOutput);
          }
        }
      };

      // Start monitoring and playing
      monitorOutput();
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 1);

      // Play the audio
      testAudio.play().catch(e => console.error("Error playing test audio:", e));

      // Clean up and reset output volume
      setTimeout(() => {
        setOutputVolume(0);
        audioContext.close();
        testAudio.pause();
      }, 1500);

    } catch (error) {
      console.error('Error testing audio output:', error);

      // Fallback: try to play a silent audio file to test the speaker
      try {
        const testAudio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+LvwmlwFGlKJUJtYr9wIArAqlEtbj7/iCQhOHY+8LVLHWd7QWyHGg==');
        if (selectedSettingsSpeaker?.deviceId && testAudio.setSinkId) {
          await testAudio.setSinkId(selectedSettingsSpeaker.deviceId);
        }
        testAudio.volume = 0.3;

        // Monitor volume during playback
        testAudio.addEventListener('play', () => {
          const duration = 1000; // 1 second
          const startTime = Date.now();
          const simulateVolume = () => {
            const elapsed = Date.now() - startTime;
            const progress = elapsed / duration;
            if (progress < 1) {
              // Simulate volume based on playback progress
              const volume = Math.sin(progress * Math.PI) * 50; // Peak at middle, fade to 0
              setOutputVolume(volume);
              requestAnimationFrame(simulateVolume);
            } else {
              setOutputVolume(0);
            }
          };
          simulateVolume();
        });

        testAudio.play();
      } catch (fallbackError) {
        console.error('Fallback audio test also failed:', fallbackError);
      }
    }
  }, [selectedSettingsSpeaker, setOutputVolume]);

  const handleSelectImageClick = (e) => {
    e.preventDefault();
    fileInputRef.current?.click();
  }

  const handleDeleteClick = (e, background) => {
    console.log("Background to delete:", background);
    e.preventDefault(); // Prevent default button behavior
    e.stopPropagation(); // Prevent triggering the background selection
    setBackgroundToDelete(background);
    setConfirmDeleteOpen(true);
  }

  const handleDefaultDeleteClick = (e, background) => {
    console.log("Background to delete:", background);
    e.preventDefault(); // Prevent default button behavior
    e.stopPropagation(); // Prevent triggering the background selection
    setDefaultbackgroundToDelete(background);
    setConfirmDeleteOpen(true);
  }

  const handleConfirmDelete = async () => {
    if (!backgroundToDelete && !defaulBtackgroundToDelete) return;

    try {
      if (backgroundToDelete) {
        await dispatch(deleteBackground({
          params: [{ id: backgroundToDelete.id }]
        })).unwrap();
        // If the deleted background was selected, reset to none
        if (selectedSettingsBackground === backgroundToDelete.src) {
          dispatch(setSettingsEffect("none"));
          dispatch(setSettingsBackground(""));
        }
        setConfirmDeleteOpen(false);
        setBackgroundToDelete(null);
      }

      if (defaulBtackgroundToDelete) {
        await dispatch(deleteDefaultBackground({
          params: [{ id: defaulBtackgroundToDelete.id }]
        })).unwrap();
        // If the deleted background was selected, reset to none
        if (selectedSettingsBackground === defaulBtackgroundToDelete.src) {
          dispatch(setSettingsEffect("none"));
          dispatch(setSettingsBackground(""));
        }
        setConfirmDeleteOpen(false);
        setDefaultbackgroundToDelete(null);
      }


    } catch (error) {
      console.error("Error deleting background:", error);

    }
  }


  const handleSelectDefaultImageClick = (e) => {
    e.preventDefault();
    fileDefaultInputRef.current?.click();
  }




  // Helper function to determine if an effect/background combination is active
  const isEffectActive = (effectType, backgroundValue = null) => {
    if (effectType === 'none') {
      return selectedSettingsEffect === 'none';
    }
    if (effectType === 'blur') {
      return selectedSettingsEffect === 'blur';
    }
    if (effectType === 'background') {
      return selectedSettingsEffect === 'background' &&
        (backgroundValue ? selectedSettingsBackground === backgroundValue : true);
    }
    return false;
  };

  // Handle effect selection
  const handleEffectSelect = (e, effectType, backgroundValue) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    dispatch(setSettingsEffect(effectType));
    if (backgroundValue) {
      dispatch(setSettingsBackground(backgroundValue));
    }
  };

  const findDeviceById = (devices, deviceId) => {
    return devices.find((device) => device.deviceId === deviceId);
  };

  const handleMicSelect = (deviceId) => {
    const selectedDevice = findDeviceById(mics, deviceId);
    if (selectedDevice) {
      dispatch(setSettingsMic(selectedDevice));
    }
  }

  const handleSpeakerSelect = (deviceId) => {
    const selectedDevice = findDeviceById(speakers, deviceId);
    if (selectedDevice) {
      dispatch(setSettingsSpeaker(selectedDevice));
    }
  }

  const handleCameraSelect = (deviceId) => {
    const selectedDevice = findDeviceById(cameras, deviceId);
    if (selectedDevice) {
      dispatch(setSettingsCamera(selectedDevice));
    }
  }
  const handleTestLiveKit = () => {
    dispatch(setTestModal("livekit_tester"));
  }

  return (
    <>

      <DialogContent className='dialog-content-scroll'>
        <div className="manual-test-livekit">
          <span className='test-livekit-text'>
            {t("check_test_livekit")}
          </span>
          <div className="test-livekit-active">
            <button className="test-livekit-button" type="button" onClick={handleTestLiveKit} >
              <span className="material-symbols-outlined">
                build
              </span>
              {t("run_test")}
            </button>
          </div>
        </div>
        <div className="invitation-tabs" style={{ marginBottom: "2rem" }}>
          <button onClick={() => dispatch(setActiveTab("audio"))}
            type="button"
            className={`invitation-tab ${activeTab === "audio" ? "active" : ""}`}
            sx={
              { width: "50%" }
            }
          >
            <span className="material-symbols-outlined">
              headphones
            </span>
            {t("Audio")}
          </button>
          <button onClick={() => dispatch(setActiveTab("video"))}
            type="button"
            className={`invitation-tab  ${activeTab === "video" ? "active" : ""}`}
            sx={
              { width: "50%" }
            }
          >
            <span class="material-symbols-outlined">
              photo_camera_front
            </span>
            {t("Vidéo-Arrière-plan")}
          </button>
        </div>

        <Grid container spacing={2}>
          {activeTab === "audio" && (
            <>
              <div className="audio-container">
                <Grid item xs={6} className="grid-xs-6">
                  <Box display="flex" alignItems="center">
                    <MicIcon
                      sx={{ marginRight: 1, fontSize: "large" }}
                    />
                    <InputLabel>{t("microphone")}</InputLabel>
                  </Box>
                  {mics.length > 0 && (
                    <FormControl fullWidth>
                      <Select className="config-select"
                        value={selectedSettingsMic.deviceId}
                        onChange={(e) => handleMicSelect(e.target.value)}
                        sx={{
                          height: "2.8125rem",
                          marginTop: "0.625rem",
                          marginBottom: "0.625rem",
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {mics.map((device) => (
                          <MenuItem
                            key={device.deviceId}
                            value={device.deviceId}
                          >
                            {device.label ||
                              `Microphone ${device.deviceId}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <div className='settings-tests settings-test-microphone'>
                    <div
                      ref={inputVolumeBarRef}
                      className='subsettings-test-microphone'
                      style={{ width: inputVolumeBarRef + '%', backgroundSize: inputVolumeBarRef > 1 ? `${10000 / inputVolumeBarRef}% 100%` : '0% 100%' }}
                    ></div>
                  </div>
                </Grid>

                <Grid item xs={6} className="grid-xs-6">
                  <Box display="flex" alignItems="center">
                    <VolumeUpIcon
                      sx={{ marginRight: 1, fontSize: "large" }}
                    />
                    <InputLabel>{t("speaker")}</InputLabel>
                  </Box>
                  {speakers.length > 0 && (
                    <FormControl fullWidth>
                      <Select className="config-select"
                        value={selectedSettingsSpeaker.deviceId}
                        onChange={(e) => handleSpeakerSelect(e.target.value)}
                        sx={{
                          height: "2.8125rem",
                          marginTop: "0.625rem",
                          marginBottom: "0.625rem",
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                      >
                        {speakers.map((device) => (
                          <MenuItem
                            key={device.deviceId}
                            value={device.deviceId}
                          >
                            {device.label ||
                              `Haut parleur ${device.deviceId}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <div className="settings-tests settings-test-haut-parleur">
                    <div
                      className="subsettings-test-haut-parleur"
                      style={{ width: outputVolume + "%" }}
                    ></div>
                  </div>
                  <Button className="test-sound-btn" onClick={handleTestOutputAudio}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="12" viewBox="0 0 13 12" fill="none">
                      <path d="M11.4685 5.08775C12.2556 5.44122 12.2556 6.55878 11.4685 6.91225L1.40964 11.4292C0.747978 11.7263 -5.35126e-07 11.2422 -5.03421e-07 10.5169L-1.08538e-07 1.48307C-7.68338e-08 0.757751 0.747977 0.273701 1.40964 0.570823L11.4685 5.08775Z" fill="#60D744" />
                    </svg>
                    {t("test_audio")}
                  </Button>
                </Grid>
              </div>
            </>
          )}
          {activeTab === "video" && (
            <>
              <Grid item xs={12} className="grid-xs-12">
                <Box display="flex" alignItems="center">
                  <VideocamIcon
                    sx={{ marginRight: 1, fontSize: "large" }}
                  />
                  <InputLabel>{t("camera")}</InputLabel>
                </Box>
                {cameras.length > 0 && (
                  <FormControl fullWidth>
                    <Select className="config-select"
                      value={selectedSettingsCamera?.deviceId}
                      onChange={(e) => handleCameraSelect(e.target.value)}
                      sx={{
                        height: "2.8125rem",
                        marginTop: "0.625rem",
                        marginBottom: "0.625rem",
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      {cameras.map((device) => (
                        <MenuItem
                          key={device.deviceId}
                          value={device.deviceId}
                        >
                          {device.label ||
                            `Camera ${device.deviceId}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <div className="settings-tests settings-test-camera">

                  {cameraTrack && (
                    <video
                      id="camera-vid-processed"
                      autoPlay
                      muted
                      ref={cameraRef}
                      style={{ width: '100%', height: 'auto', maxHeight: '200px' }}
                    />
                  )}
                  {!cameraTrack && (
                    <div style={{
                      width: '100%',
                      height: '12.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f0f0f0',
                      color: '#666'
                    }}>
                      {cameraLoading ? 'Loading camera...' : 'Camera not available'}
                    </div>
                  )}
                </div>
              </Grid>
              {tenantHasFeature("CAMERA_EFFECT_ENABLED") && (
                <Grid container className="effect-box">
                  <Grid item xs={6} className="grid-xs-6">
                    <Box className="left-box">
                      <span className="box-title">{t("blur_and_custom_backgrounds")}</span>
                      <div className="effect-types">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />

                        {/* None Effect Button */}
                        <button
                          onClick={(e) => handleEffectSelect(e, "none")}
                          className={`${isEffectActive("none") ? 'active' : ''}`}
                        >
                          <span className="material-symbols-outlined">
                            block
                          </span>
                          <span className="effect-name">{t("none")}</span>
                        </button>

                        {/* Blur Effect Button */}
                        <button
                          onClick={(e) => handleEffectSelect(e, "blur")}
                          className={`${isEffectActive("blur") ? 'active' : ''}`}
                        >
                          <span className="material-symbols-outlined">
                            blur_on
                          </span>
                          <span className="effect-name">{t("blur")}</span>
                        </button>

                        {/* Add Custom Background Button */}
                        <button onClick={handleSelectImageClick}>
                          <span className="material-symbols-outlined">
                            add_photo_alternate
                          </span>
                          <span className="effect-name">{t("add")}</span>
                        </button>
                      </div>

                      {/* Custom Backgrounds Gallery */}
                      {customBackgrounds.length > 0 && (
                        <div className="image-gallery-container custom-image">
                          {customBackgrounds.map(bg => {
                            const isActive = isEffectActive("background", bg.src);
                            return (
                              <div key={bg.id} className="custom-background-wrapper">
                                <img
                                  className={`custom-background-image ${isActive ? 'active' : ''}`}
                                  src={bg.src}
                                  onClick={(e) => handleEffectSelect(e, "background", bg.src)}
                                  alt="Custom background"
                                />
                                <button
                                  type="button"
                                  className="delete-background-btn"
                                  onClick={(e) => handleDeleteClick(e, bg)}
                                  aria-label={t("delete")}
                                >
                                  <DeleteIcon className="delete-icon" />                            </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={6} className="grid-xs-6">
                    <Box className="right-box">
                      <span className="box-title">{t("default_backgrounds")}</span>
                      <div className="effect-types">
                        <input
                          type="file"
                          ref={fileDefaultInputRef}
                          onChange={handleDefaultFileChange}
                          accept="image/*"
                          style={{ display: 'none' }}
                        />
                        {/* Add Custom Background Button */}
                        <button onClick={handleSelectDefaultImageClick}>
                          <span className="material-symbols-outlined">
                            add_photo_alternate
                          </span>
                          <span className="effect-name">{t("add")}</span>
                        </button>
                      </div>
                      <div className="image-gallery-container default-image">
                        {publicDefaultBackgrounds.map(bg => {
                          const backgroundPath = `/professional_background_images/${bg}`;
                          const isActive = isEffectActive("background", backgroundPath);
                          return (
                            <div key={bg.id} className="custom-background-wrapper">
                              <img
                                className={`custom-background-image ${isActive ? 'active' : ''}`}
                                src={backgroundPath}
                                onClick={(e) => handleEffectSelect(e, "background", backgroundPath)}
                                alt="Custom background"
                              />
                            </div>
                          );
                        })}

                        {defaultBackgrounds.length > 0 && defaultBackgrounds.map(bg => {
                          const isActive = isEffectActive("background", bg.src);
                          return (
                            <div key={bg.id} className="custom-background-wrapper">
                              <img
                                className={`custom-background-image ${isActive ? 'active' : ''}`}
                                src={bg.src}
                                onClick={(e) => handleEffectSelect(e, "background", bg.src)}
                                alt="Custom background"
                              />
                              <button
                                type="button"
                                className="delete-background-btn"
                                onClick={(e) => handleDefaultDeleteClick(e, bg)}
                                aria-label={t("delete")}
                              >
                                <DeleteIcon className="delete-icon" />
                              </button>
                            </div>
                          );
                        })}
                      </div>

                    </Box>
                  </Grid>
                </Grid>
              )}


              {// Hidden canvas rendering components
              }
              <video
                ref={hiddenVideoRef}
                style={{
                  position: 'absolute',
                  top: '-9999px',
                  left: '-9999px',
                }}
                muted
                playsInline
              />
              <canvas
                ref={hiddenCanvasRef}
                style={{
                  position: 'absolute',
                  top: '-9999px',
                  left: '-9999px',
                }}
              />
              {selectedSettingsBackground && (<img
                ref={backgroundImageRef}
                src={selectedSettingsBackground}
                crossOrigin='anonymous'
                onLoad={() => setIsImageReady(true)}
                onError={() => setIsImageReady(false)}
                style={{
                  position: 'absolute',
                  top: '-9999px',
                  left: '-9999px',
                }}
              />)}
            </>
          )}
        </Grid>
      </DialogContent>


      {activeTab === "video" && (
        <>
          <DialogActions
            sx={{ marginTop: "0.5rem" }}
          >
            <Button
              sx={{
                color: "#69D945",
                textTransform: "none",
              }}
              onClick={() => {
                onClose();
              }}
              disabled={cameraLoading}
            >
              {t("cancel")}
            </Button>
            {cameraLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="16.5%"
              >
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Button
                sx={{
                  color: "white !important",
                  borderRadius: "0.5rem",
                  background: 'var(--peoplmap-primary)',
                  display: "flex",
                  padding: "0.6875 1.375rem",
                  justifyContent: "center",
                  alignItems: "center",
                  textTransform: "none",
                  '&:hover': {
                    background: "#53c03d",
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleSave();
                }}
              >
                {t("save")}
              </Button>
            )}
          </DialogActions>

          {/* Delete Background Confirmation Modal */}
          <ActionConfirmation
            open={confirmDeleteOpen}
            title={t("background_deletion")}
            msg={t("delete_background_confirmation_dialog_message")}
            cancelText={t("cancel")}
            confirmText={t("delete")}
            variant="destructive"
            onClose={() => {
              setConfirmDeleteOpen(false);
              setBackgroundToDelete(null);
              setDefaultbackgroundToDelete(null);
            }}
            onConfirm={handleConfirmDelete}
          />
        </>
      )}
    </>
  );
}